import TransactionEmpty from "@images/empty/transactions_empty.svg"
import { word } from "@modules/core/utils/i18n"
import { ExportModal } from "@modules/finances/components/exportModal"
import { TransactionTableFilter } from "@modules/finances/components/transactionTableFilter"
import { headerColumn, userTransactionsColumns, valueColumn } from "@modules/finances/components/transactionsColumns"
import { UserTransactionsDetailsLayout } from "@modules/finances/components/userTransactionsDetailsLayout"
import { useTransactions } from "@modules/finances/hooks/useFinance"
import { useFinanceSidebar } from "@modules/finances/hooks/useFinanceSidebar"
import { usePeriodParam } from "@modules/finances/hooks/usePeriod"
import { ApiFinanceType, PageStripeType, TransactionStatus } from "@modules/finances/types/financesType"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { DataTable, renderTableColumns } from "@modules/ui/components/dataTables/datatable"
import { EmptyView } from "@modules/ui/components/emptyView"
import { HUButton } from "@modules/ui/components/huButton"
import { HUDropdown } from "@modules/ui/components/huDropdown"
import { HUText } from "@modules/ui/components/huText"
import { HURow } from "@modules/ui/components/layout"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { usePersistedDatatable } from "@modules/utils/hooks/useDataTable"
import { isInArray } from "@modules/utils/isInArray"
import { useQueryParam } from "@modules/utils/navigation/useQueryParam"
import { Button } from "primereact/button"
import { Column } from "primereact/column"
import { DataTableStateEvent, DataTableValue } from "primereact/datatable"
import { useEffect, useState } from "react"
import styled, { DefaultTheme, useTheme } from "styled-components"

export const UserTransactionsTable: React.FC = () => {
  const { size, setSize } = usePersistedDatatable(20, true)
  const language = useLanguage()
  const [displayModal, setDisplayModal] = useState(false)
  const theme = useTheme()
  const { sidebarNavigateTo } = useSidebar()
  const [selectedTransaction, setSelectedTransaction] = useState<DataTableValue>()
  const [period] = usePeriodParam()
  const [transactionsTab] = useQueryParam("tab")
  const { navigateToFinanceFeesLayout } = useFinanceSidebar()
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const [pageStripe, setPageStripe] = useState<PageStripeType[]>([
    {
      currentPage: undefined,
      previousPage: undefined,
      nextPage: undefined,
    },
  ])

  const sizeOptions = [
    { value: 20, label: "20" },
    { value: 30, label: "30" },
    { value: 50, label: "50" },
  ]

  const {
    data: transactions,
    isLoading,
    nextPage,
  } = useTransactions(
    size,
    period,
    pageStripe[currentIndex].currentPage,
    isInArray(Object.values(TransactionStatus), transactionsTab) ? (transactionsTab as TransactionStatus) : undefined,
  )

  useEffect(() => {
    setCurrentIndex(0)
    setPageStripe([
      {
        currentPage: undefined,
        previousPage: undefined,
        nextPage,
      },
    ])
  }, [transactionsTab, size, period])

  useEffect(() => {
    if (nextPage) {
      setPageStripe((prev) => {
        const updatedPageStripe = [...prev]
        updatedPageStripe[currentIndex] = {
          ...updatedPageStripe[currentIndex],
          nextPage,
        }
        return updatedPageStripe
      })
    }
  }, [nextPage])

  const handleSetSize = (event: DataTableStateEvent) => {
    setSize(event.rows)
  }

  const handlePreviousPage = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1)
    }
  }

  const handleNextPage = () => {
    if (pageStripe[currentIndex].nextPage) {
      setCurrentIndex((prev) => prev + 1)
      setPageStripe((prev) => {
        const updatedPageStripe = [...prev]
        updatedPageStripe.push({
          currentPage: pageStripe[currentIndex].nextPage,
          previousPage: pageStripe[currentIndex].currentPage,
          nextPage: undefined,
        })
        return updatedPageStripe
      })
    }
  }

  const onSelectedTransaction = (e: DataTableValue) => {
    sidebarNavigateTo({
      content: <UserTransactionsDetailsLayout transactionId={e.transactionId} />,
      onHide: () => {
        setSelectedTransaction(undefined)
        return Promise.resolve(true)
      },
      replaceAllComponents: true,
    })
    setSelectedTransaction(e)
  }

  return (
    <div className="flex flex-column flex-grow-1" style={{ gap: 28 }}>
      <div className={"flex flex-wrap flex-row justify-content-end align-items-center gap-2 xl:flex-row md:gap-1"}>
        <TransactionTableFilter filters={["ALL", ...Object.values(TransactionStatus)]} />
        <HUButton
          icon={{ iconView: <span className={`p-button-icon p-c pi pi-reply`} /> }}
          type="Left_Icon"
          colorType="Primary"
          size="S"
          text={word("global.export")}
          onClick={() => setDisplayModal(true)}
        />
      </div>
      {isLoading ? (
        <LoadingSpinner />
      ) : transactions.length === 0 ? (
        <EmptyView
          image={{ src: TransactionEmpty, background: theme.neutralColor100, padding: "2rem" }}
          title={word("finances.transactions.empty.title")}
          description={word("finances.transactions.empty.message")}
          textAlign="left"
          className="mt-4"
          style={{
            display: "flex",
            flexDirection: "row-reverse",
            alignItems: "center",
            justifyContent: "center",
            flexGrow: 1,
            gap: "2rem",
          }}
        />
      ) : (
        <StyledDatatable
          value={[...transactions]}
          dataKey="transactionId"
          paginator
          lazy
          currentPageReportTemplate={""}
          rows={size}
          totalRecords={size}
          onPage={(event) => {
            handleSetSize(event)
          }}
          paginatorTemplate=""
          emptyMessage={word("global.no_result")}
          loading={isLoading}
          selectionMode="single"
          selection={selectedTransaction}
          onSelectionChange={(e) => onSelectedTransaction(e.value)}
          metaKeySelection={true}
          footer={
            (pageStripe[currentIndex].previousPage || pageStripe[currentIndex].nextPage || currentIndex === 1) && (
              <FooterRow align gap={14}>
                <HURow gap={8}>
                  <HUButton
                    type="Default"
                    size="S"
                    colorType="Quaternary"
                    onClick={() => handlePreviousPage()}
                    disabled={pageStripe[currentIndex].previousPage === undefined && currentIndex !== 1}
                    text={word("global.previous")}
                  />
                  <HUButton
                    type="Default"
                    size="S"
                    colorType="Quaternary"
                    onClick={() => handleNextPage()}
                    disabled={pageStripe[currentIndex].nextPage === undefined}
                    text={word("global.next")}
                  />
                </HURow>
                <Separator />
                <div className="flex align-items-center gap-2">
                  <HUText fontStyle="LM" color={theme.neutralColor600}>
                    {word("finances.page.table.footer.label")}
                  </HUText>
                  <HUDropdown value={size} options={sizeOptions} onChange={(e) => setSize(e)} itemFontSize="0.9rem" />
                </div>
              </FooterRow>
            )
          }
        >
          {renderTableColumns(
            [...userTransactionsColumns],
            headerColumn,
            valueColumn,
            theme as DefaultTheme,
            language,
            undefined,
            {
              key: "fee",
              onClick: navigateToFinanceFeesLayout,
            },
          )}
          <Column
            body={(transaction) => (
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  icon="pi pi-eye"
                  severity="secondary"
                  outlined
                  className="p-button-rounded mr-2"
                  onClick={() => {
                    onSelectedTransaction(transaction)
                  }}
                />
              </div>
            )}
          />
        </StyledDatatable>
      )}
      <ExportModal onHide={() => setDisplayModal(false)} visible={displayModal} financeApiType={ApiFinanceType.USER} />
    </div>
  )
}

const StyledDatatable = styled(DataTable)`
  .p-paginator-bottom {
    border: none;
  }
`

const FooterRow = styled(HURow)`
  justify-content: center;

  button {
    min-width: 110px;
  }

  .p-inputtext {
    font-size: 0.9rem;
  }
`

const Separator = styled.div`
  display: flex;
  width: 1px;
  height: 42px;
  background-color: ${({ theme }) => theme.neutralColor200};
`
